import { Page } from "../../components/layout/Page";
import { Breadcrumb } from "../../components/layout/breadcrumb";
import {
  Anchor,
  Box,
  Button,
  Drawer,
  LoadingOverlay,
  Table,
  Text,
  Title,
} from "@mantine/core"
import { CheckIcon, CopyIcon, FileTextIcon, PaperPlaneTiltIcon, PencilSimpleIcon, ScissorsIcon, WalletIcon } from "@phosphor-icons/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { getModuleUrl } from "@dehub/theme"
import { httpRequest } from "../../apis/invoice"
import useSwr from "swr"
import { InvoiceApi } from "../../apis"
import { AmendmentBanner } from "../../components/invoice-amendment/AmendmentBanner"
import { AmendmentHistory } from "../../components/invoice-amendment/AmendmentHistory"
import {
  buildProposalDisplayLines,
  computeAmendmentLineChanges,
  lineChangeKeyForInvoiceLine,
} from "../../components/invoice-amendment/amendment-line-diff"
import { handleAmendError } from "../../components/invoice-amendment/amend-error"
import { SafeRelatedReturns } from "../../components/invoice-detail/SafeRelatedReturns"
import { DeliverCopyModal } from "../../components/invoice-actions/deliver-copy-modal"
import { ReminderDrawer } from "../../components/invoice-actions/reminder-drawer"
import {
  RegisterPayment,
  REGISTER_PAYMENT_DRAWER_STYLES,
} from "../../components/register-payment/register-payment"
import { usePermission } from "../../components/permission"
import { usePdfDownload } from "../../hooks/usePdfDownload"
import { StatusBadge, resolveInvoiceStatusColor } from "../../components/ui/status-badge"
import modalChrome from "../../components/ui/app-modal.module.css"
import grid from "../../components/ui/data-grid.module.css";
import { IGeneral } from "../../interfaces/IGeneral"
import { IReference } from "../../interfaces/IReference"
import { currencyFormat } from "../../utils"
import { dateFormat, dateTimeFormat } from "../../utils/date"
import { paymentTermLabel } from "../../utils/payment-term-label"
import { renderBusinessName } from "../../utils/render-business-name"
import { renderName } from "../../utils/render-name"
import { message } from "../../utils/Message"
import classes from "./details.module.css"
import { dueDay, daysUntilDue } from "../../utils/due-day"
import { ReasonTooltip } from "../../components/ui/reason-tooltip";

type StepKey = "draft" | "sent" | "viewed" | "confirmed" | "paid" | "closed"

function stepStates(invoiceStatus?: string, paymentStatus?: string): Record<StepKey, "done" | "active" | "todo"> {
  const result: Record<StepKey, "done" | "active" | "todo"> = {
    draft: "todo",
    sent: "todo",
    viewed: "todo",
    confirmed: "todo",
    paid: "todo",
    closed: "todo",
  }
  const order: StepKey[] = ["draft", "sent", "viewed", "confirmed", "paid", "closed"]
  let activeIdx = 0
  switch (invoiceStatus) {
    case "DRAFT":
      activeIdx = 0
      break
    case "SENT":
      activeIdx = 1
      break
    case "VIEWED":
      activeIdx = 2
      break
    case "CONFIRMED":
      activeIdx = 3
      break
    case "CLOSED":
      activeIdx = 5
      break
    default:
      activeIdx = paymentStatus === "CLOSED" ? 4 : 3
  }
  // Payment statuses: PENDING / DIVIDED (part paid) / CLOSED (fully paid).
  if ((paymentStatus === "CLOSED" || paymentStatus === "DIVIDED") && activeIdx < 4) activeIdx = 4
  order.forEach((k, i) => {
    if (i < activeIdx) result[k] = "done"
    else if (i === activeIdx) result[k] = "active"
  })
  return result
}


function fileExt(nameOrUrl?: string | null): string {
  if (!nameOrUrl) return "FILE"
  const clean = nameOrUrl.split("?")[0] ?? nameOrUrl
  const base = clean.split("/").pop() ?? clean
  const parts = base.split(".")
  if (parts.length < 2) return "FILE"
  return (parts.pop() ?? "FILE").slice(0, 4).toUpperCase()
}

export const SalesInvoiceDetails = () => {
  const params = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation(["invoice", "common"])

  const INV_LIST = usePermission("INV_LIST")
  const INV_REG_PAYMENT = usePermission("INV_REG_PAYMENT")
  const INV_EDIT_DRAFT = usePermission("INV_EDIT_DRAFT")
  const INV_PDF = usePermission("INV_PDF")
  const INV_AMEND = usePermission("INV_AMEND")

  const invoiceStatusRef = useSelector((state: { general: IGeneral }) => state.general.invoiceStatus)
  const paymentStatusRef = useSelector((state: { general: IGeneral }) => state.general.invoicePaymentStatus)
  const paymentTermConditions = useSelector((state: { general: IGeneral }) => state.general.paymentTermConditions)

  const { data, isLoading, mutate } = useSwr<any>(
    params.id !== "new" && INV_LIST.hasAccess ? `/api/invoice/${params.id}` : null,
    params.id !== "new" && INV_LIST.hasAccess ? async () => await InvoiceApi.get(`${params.id}`) : null,
  )

  const { data: amendability, mutate: mutateAmendability } = useSwr(
    params.id && INV_LIST.hasAccess ? `/api/invoice/${params.id}/amendability` : null,
    () => InvoiceApi.amendability(`${params.id}`),
  )

  const { download: downloadPdf, isLoading: isPdfLoading } = usePdfDownload({ fetcher: InvoiceApi.pdf })
  const [action, setAction] = useState<any[]>([])
  const [reminderOpened, setReminderOpened] = useState(false)
  const [copyOpened, setCopyOpened] = useState(false)
  const [withdrawing, setWithdrawing] = useState(false)
  const [lineView, setLineView] = useState<"current" | "proposal">("current")

  if (isLoading) return <Box pos="relative" mih={320}><LoadingOverlay visible /></Box>
  if (!INV_LIST.hasAccess) return <Page>{INV_LIST.accessDenied()}</Page>

  const d: any = data ?? {}
  const steps = stepStates(d.invoiceStatus, d.paymentStatus)
  const stepOrder: StepKey[] = ["draft", "sent", "viewed", "confirmed", "paid", "closed"]

  const lines: any[] = Array.isArray(d.lines) ? d.lines : []
  const additionalLines: any[] = Array.isArray(d.additionalLines) ? d.additionalLines : []
  const attachmentsRaw: any[] = Array.isArray(d.attachments) ? d.attachments : []
  const attachments = attachmentsRaw.filter((a) => Boolean(a?.url || a?.name))
  const descriptionText = d.description?.trim() ?? ""
  const hasAttachmentsCard = Boolean(descriptionText) || attachments.length > 0
  const pending = amendability?.enabled ? amendability.pending : undefined
  const lineChanges = pending
    ? computeAmendmentLineChanges(pending, lines, additionalLines)
    : []
  const changeByKey = new Map(lineChanges.map((c) => [c.key, c]))
  const proposalCode = pending?.refCode?.includes("-")
    ? pending.refCode.split("-").slice(-1)[0]
    : "A1"
  const showProposal = Boolean(pending && lineView === "proposal")
  const displayLines = showProposal
    ? buildProposalDisplayLines(pending!, lines, additionalLines)
    : lines.map((li) => ({
        ...li,
        _changeKey: lineChangeKeyForInvoiceLine(li, false),
        _was: null,
        _removed: false,
      }))

  const itemsTotal = Number(d.itemsTotal ?? 0)
  const vatAmount = Number(d.vatAmount ?? 0)
  const taxAmount = Number(d.taxAmount ?? 0)
  const shippingAmount = Number(d.shippingAmount ?? 0)
  const discountAmount = Number(d.discountAmount ?? 0)
  const grandTotal = Number(d.totalAmount ?? d.confirmedAmount ?? 0)
  const additionalAmount = Array.isArray(d.additionalLines)
    ? d.additionalLines.reduce((s: number, it: any) => s + Number(it?.quantity ?? 0) * Number(it?.price ?? 0), 0)
    : 0
  const addOns = taxAmount + additionalAmount + shippingAmount
  const exclusiveGuess = itemsTotal + vatAmount + addOns - discountAmount
  const inclusiveGuess = itemsTotal + addOns - discountAmount
  const vatIncluded = vatAmount > 0 && Math.abs(inclusiveGuess - grandTotal) < Math.abs(exclusiveGuess - grandTotal)
  const runningBeforeDiscount = itemsTotal + addOns + (vatIncluded ? 0 : vatAmount)
  const reconcileDiscount = Math.max(0, Math.round((runningBeforeDiscount - grandTotal) * 100) / 100)
  const discountPct = itemsTotal > 0 ? Math.round((reconcileDiscount / itemsTotal) * 1000) / 10 : 0
  const paid = Number(d.paidAmount ?? 0)
  const proposalGrand = pending ? Number(pending.newTotal) : grandTotal
  const proposalPrevious = pending ? Number(pending.previousTotal) : grandTotal
  const proposalVat =
    showProposal && pending?.newVat != null && Number(pending.newVat) > 0
      ? Number(pending.newVat)
      : null
  const proposalVatExclusive = proposalVat != null && pending?.vatMode === "EXCLUSIVE"
  const proposalVatInclusive = proposalVat != null && pending?.vatMode === "INCLUSIVE"
  const remaining = Number(d.amountToPay ?? Math.max(0, grandTotal - paid))
  const remainingDays: number | null =
    typeof d.remainingDays === "number" ? d.remainingDays : daysUntilDue(d.paymentDate)
  const isOverdue = remainingDays != null && remainingDays < 0
  const paidPct = grandTotal > 0 ? Math.min(100, Math.max(0, (paid / grandTotal) * 100)) : 0
  const paidPctRounded = Math.round(paidPct)

  const sender = d.senderBusiness ?? d.senderBusinessProfile ?? null
  const receiver = d.receiverBusiness ?? null

  const invStatusRef = invoiceStatusRef?.find((x: IReference) => x.code === d.invoiceStatus)
  const payStatusRef = paymentStatusRef?.find((x: IReference) => x.code === d.paymentStatus)
  const paidLike = d.paymentStatus === "CLOSED" || d.paymentStatus === "DIVIDED"

  const contactEmail = receiver?.email ?? sender?.email ?? null
  const contactPhone = receiver?.phone ?? sender?.phone ?? null
  const contactAddress = receiver?.address ?? sender?.address ?? null

  const poCode = d.salesOrderCode ?? d.purchaseSalesCode ?? null
  const createdDate = d.createdAt ? dateFormat(d.createdAt) : null
  const confirmedDate = d.confirmedDate ? dateFormat(d.confirmedDate) : null
  const createdConfirmed =
    createdDate && confirmedDate && createdDate !== confirmedDate
      ? `${createdDate} / ${confirmedDate}`
      : confirmedDate ?? createdDate ?? "-"

  const receiverLabel = (() => {
    const name = renderBusinessName(receiver, "-")
    const ttd = receiver?.regNumber ?? receiver?.partner?.regNumber
    return ttd ? `${name} · ${t("invoice:detail.meta.ttd")} ${ttd}` : name
  })()

  const paymentTerms = paymentTermLabel(
    d.paymentTermName ?? d.paymentTerm ?? d.paymentTerms ?? null,
    paymentTermConditions,
    t("invoice:detail.meta.termsDefault")
  )

  // Pay-on-receipt has no countdown: the due date is the delivery, not a day count.
  const payOnReceipt = d.paymentTermConfigType === "COD" && !paidLike
  const progressMeta =
    payOnReceipt
      ? t("invoice:detail.kpi.paidProgressOnReceipt", { pct: paidPctRounded })
      : remainingDays == null
      ? t("invoice:detail.kpi.paidPctOnly", { pct: paidPctRounded })
      : isOverdue
        ? t("invoice:detail.kpi.paidProgressOverdue", { pct: paidPctRounded, days: Math.abs(remainingDays) })
        : remainingDays === 0
          ? t("invoice:detail.kpi.paidProgressToday", { pct: paidPctRounded })
          : t("invoice:detail.kpi.paidProgress", { pct: paidPctRounded, days: remainingDays })

  const handleWithdrawAmendment = async () => {
    if (!params.id || !amendability?.pending?.id) return
    try {
      setWithdrawing(true)
      await InvoiceApi.withdrawAmendment(`${params.id}`, amendability.pending.id)
      message.success(t("invoice:amend.withdrawnToast"))
      await Promise.all([mutate(), mutateAmendability()])
    } catch (error) {
      handleAmendError(error)
    } finally {
      setWithdrawing(false)
    }
  }

  return (
    <Page>
      <Box className={classes.pageHdr}>
        <Box className={classes.pageHdrLeft}>
          <Breadcrumb data={[
              { label: t("invoice:breadcrumb.salesInvoice") },
              { label: t("invoice:detail.crumbOpen"), to: "/sales-invoice" },
              { label: d.refCode ?? "-" },
            ]} />

          <Box className={classes.titleRow}>
            <Title order={1} className={classes.pageTitle}>
              {d.refCode ?? "-"}
            </Title>
            {invStatusRef && (
              <StatusBadge
                color={resolveInvoiceStatusColor(d.invoiceStatus, invStatusRef.color)}
                label={invStatusRef.name ?? d.invoiceStatus}
              />
            )}
            {payStatusRef && (
              paidLike ? (
                <Box component="span" className={classes.payPill}>
                  {t(`invoice:paymentStatus.${d.paymentStatus}`, { defaultValue: payStatusRef.name ?? d.paymentStatus })}
                </Box>
              ) : (
                <StatusBadge
                  color={resolveInvoiceStatusColor(d.paymentStatus, payStatusRef.color)}
                  label={t(`invoice:paymentStatus.${d.paymentStatus}`, { defaultValue: payStatusRef.name ?? d.paymentStatus })}
                />
              )
            )}
            {amendability?.enabled && amendability.pending && (
              <Box component="span" className={`${classes.statusPill} ${classes.statusPillAmendPending}`}>
                <Box component="span" className={classes.statusPillDot} />
                {t("invoice:amend.pendingStatusPill")}
              </Box>
            )}
          </Box>

          <Text className={classes.pageSub}>
            {renderBusinessName(receiver, "-")}
            {confirmedDate && (
              <>
                {" "}
                · {t("invoice:detail.subtitleConfirmed", { date: confirmedDate })}
              </>
            )}
            {d.paymentDate && (
              <>
                {" "}
                ·{" "}
                <Text
                  component="span"
                  className={isOverdue ? classes.pageSubOverdue : undefined}
                >
                  {t("invoice:detail.subtitleDue", { date: dueDay(d.paymentDate) ?? "-" })}
                </Text>
              </>
            )}
          </Text>
        </Box>

        <Box className={classes.pageHdrRight}>
            <ReasonTooltip reason={INV_PDF.reason}>
              <Button
                variant="default"
                classNames={{ root: classes.actionBtn }}
                leftSection={<FileTextIcon size={14} />}
                loading={isPdfLoading(`${params.id}`)}
                onClick={() => downloadPdf(`${params.id}`)}
              >
                {t("invoice:detail.actions.pdf")}
              </Button>
            </ReasonTooltip>
            <ReasonTooltip reason={INV_PDF.reason}>
              <Button
                variant="default"
                classNames={{ root: classes.actionBtn }}
                leftSection={<CopyIcon size={14} />}
                onClick={() => setCopyOpened(true)}
              >
                {t("invoice:detail.actions.deliverCopy")}
              </Button>
            </ReasonTooltip>
          {d.invoiceStatus === "DRAFT" && (
            <ReasonTooltip reason={INV_EDIT_DRAFT.reason}>
              <Button
                variant="default"
                classNames={{ root: classes.actionBtn }}
                leftSection={<PencilSimpleIcon size={14} />}
                onClick={() => navigate(`/sales-invoice/${params.id}`)}
              >
                {t("invoice:detail.actions.edit")}
              </Button>
            </ReasonTooltip>
          )}
          {amendability?.enabled && amendability.amendable && (
            <ReasonTooltip reason={INV_AMEND.reason}>
              <Button
                variant="default"
                classNames={{ root: classes.actionBtn }}
                leftSection={<ScissorsIcon size={14} />}
                onClick={() => navigate(`/sales-invoice/${params.id}/amend`)}
              >
                {t("invoice:amend.button")}
              </Button>
            </ReasonTooltip>
          )}
          <Button
            variant="default"
            classNames={{ root: classes.actionBtn }}
            leftSection={<PaperPlaneTiltIcon size={14} />}
            onClick={() => setReminderOpened(true)}
          >
            {t("invoice:detail.actions.remind")}
          </Button>
            <ReasonTooltip reason={INV_REG_PAYMENT.reason}>
              <Button
                color="brand"
                classNames={{ root: classes.actionBtnPrimary }}
                leftSection={<WalletIcon size={14} weight="bold" />}
                onClick={() => setAction(["payment_register"])}
              >
                {t("invoice:detail.summaryCard.registerPay")}
              </Button>
            </ReasonTooltip>
        </Box>
      </Box>

      {amendability?.enabled && amendability.pending && (
        <AmendmentBanner
          pending={amendability.pending}
          mode="seller"
          showWithdraw={INV_AMEND.hasAccess}
          withdrawing={withdrawing}
          onWithdraw={handleWithdrawAmendment}
          paidAmount={paid}
          invoiceStatus={d.invoiceStatus}
          currentLines={lines}
          currentAdditionalLines={additionalLines}
          onShowProposal={() => setLineView("proposal")}
        />
      )}

      <Box className={classes.grid}>
        <Box className={classes.colMain}>
          {/* 6. KPI */}
          <Box className={`${classes.kpiCard} ${classes.orderKpi}`}>
            <Box className={classes.kpiGrid}>
              <Box>
                <Text className={classes.kpiLabel}>{t("invoice:detail.kpi.total")}</Text>
                <Text className={classes.kpiValue}>{currencyFormat(grandTotal)}</Text>
              </Box>
              <Box>
                <Text className={classes.kpiLabel}>{t("invoice:detail.kpi.paid")}</Text>
                <Text className={classes.kpiValuePaid}>{currencyFormat(paid)}</Text>
              </Box>
              <Box>
                <Text className={classes.kpiLabel}>{t("invoice:detail.kpi.remaining")}</Text>
                {/* Улаан зөвхөн сөрөг үлдэгдэлд; хугацаа хэтэрснийг гарчгийн доорх огноо улаанаар хэлнэ. */}
                <Text className={`${classes.kpiValueRemain} ${remaining < 0 ? classes.kpiValueOverdue : ""}`}>
                  {currencyFormat(remaining)}
                </Text>
              </Box>
            </Box>
            <Box>
              <Box className={classes.kpiBar}>
                <Box className={classes.kpiBarFill} style={{ width: `${paidPct}%` }} />
              </Box>
              <Text className={classes.kpiMeta} mt={6}>
                {progressMeta}
              </Text>
            </Box>
          </Box>

          {/* 7. Line items */}
          <Box className={`${classes.card} ${classes.orderItems}`}>
            <Box className={classes.cardHead}>
              <Title order={2} className={classes.cardTitle}>
                {t("invoice:detail.itemsHead")}
              </Title>
              {pending ? (
                <Box className={classes.lineViewSeg} role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={lineView === "current"}
                    className={`${classes.lineViewTab} ${lineView === "current" ? classes.lineViewTabActive : ""}`}
                    onClick={() => setLineView("current")}
                  >
                    {t("invoice:amend.linesViewCurrent")}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={lineView === "proposal"}
                    className={`${classes.lineViewTab} ${lineView === "proposal" ? classes.lineViewTabActive : ""}`}
                    onClick={() => setLineView("proposal")}
                  >
                    {t("invoice:amend.linesViewProposal", { code: proposalCode })}
                  </button>
                </Box>
              ) : (
                <Text className={classes.cardSub}>{t("invoice:detail.lineRows", { count: lines.length })}</Text>
              )}
            </Box>
            {displayLines.length === 0 ? (
              <Box className={classes.emptyInline}>{t("common:noData")}</Box>
            ) : (
              <Box className={classes.lineTable}>
                <Table className={grid.table} horizontalSpacing={0} verticalSpacing={0} withRowBorders={false}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("invoice:detail.lineCol.product")}</Table.Th>
                      <Table.Th className={classes.num}>{t("invoice:detail.lineCol.qty")}</Table.Th>
                      <Table.Th className={classes.num}>{t("invoice:detail.lineCol.unitPrice")}</Table.Th>
                      <Table.Th className={classes.num}>{t("invoice:detail.lineCol.discount")}</Table.Th>
                      <Table.Th className={classes.num}>{t("invoice:detail.lineCol.amount")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {displayLines.map((li: any, i: number) => {
                      const qty = Number(li.quantity ?? li.qty ?? 0)
                      const price = Number(li.price ?? li.unitPrice ?? 0)
                      const disc = Number(li.discountAmount ?? li.discount ?? 0)
                      const lineTot = Number(li.totalAmount ?? li.total ?? qty * price - disc)
                      const code = li.skuCode ?? li.barcode ?? li.refCode ?? li.code ?? li.sku ?? null
                      const unit = typeof li.unit === "string" ? li.unit : (li.unit?.name ?? li.unitName ?? null)
                      const ch = showProposal ? changeByKey.get(li._changeKey) : undefined
                      const rowCls = li._removed
                        ? classes.lineRowRemoved
                        : ch?.kind === "changed"
                          ? classes.lineRowChanged
                          : ch?.kind === "added"
                            ? classes.lineRowAdded
                            : undefined
                      const was = li._was
                      const qtyBold = Boolean(was?.quantity != null)
                      const priceBold = Boolean(was?.price != null)
                      const totalBold =
                        !showProposal || Boolean(was?.total != null) || ch?.kind === "added"
                      const nameBold = Boolean(ch && !li._removed)
                      return (
                        <Table.Tr key={li.id ?? li.variantId ?? i} className={rowCls}>
                          <Table.Td>
                            <Text
                              className={`${classes.lineName}${li._removed ? ` ${classes.lineWas}` : ""}`}
                              fw={nameBold ? 600 : undefined}
                            >
                              {li.name ?? li.productName ?? li.description ?? "-"}
                            </Text>
                            {(code || unit) && (
                              <Box className={classes.lineSku}>
                                {code && <Text component="span" className={classes.lineSkuCode}>{code}</Text>}
                                {code && unit && (
                                  <Text component="span" className={classes.lineSkuUnit}>
                                    {" · "}
                                  </Text>
                                )}
                                {unit && <Text component="span" className={classes.lineSkuUnit}>{unit}</Text>}
                              </Box>
                            )}
                          </Table.Td>
                          <Table.Td className={classes.num}>
                            <Text
                              component="span"
                              fw={qtyBold ? 600 : undefined}
                              className={
                                li._removed
                                  ? classes.lineWas
                                  : ch?.kind === "changed" && !qtyBold
                                    ? classes.lineFieldMuted
                                    : undefined
                              }
                            >
                              {qty}
                            </Text>
                            {was?.quantity != null && (
                              <Text component="span" className={classes.lineWas}>{was.quantity}</Text>
                            )}
                          </Table.Td>
                          <Table.Td className={`${classes.num} ${classes.amountNowrap}`}>
                            <Text
                              component="span"
                              fw={priceBold ? 600 : undefined}
                              className={
                                li._removed
                                  ? classes.lineWas
                                  : ch?.kind === "changed" && !priceBold
                                    ? classes.lineFieldMuted
                                    : undefined
                              }
                            >
                              {currencyFormat(price, { withSymbol: false })}
                            </Text>
                            {was?.price != null && (
                              <Text component="span" className={classes.lineWas}>
                                {currencyFormat(was.price, { withSymbol: false })}
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td
                            className={`${classes.num} ${disc > 0 ? classes.lineDiscountActive : classes.lineDiscount}`}
                          >
                            {disc > 0 ? `−${currencyFormat(disc, { withSymbol: false })}` : "-"}
                          </Table.Td>
                          <Table.Td
                            className={`${classes.num} ${classes.amountNowrap} ${
                              totalBold && !li._removed ? classes.lineTotal : ""
                            } ${ch?.kind === "changed" && !totalBold ? classes.lineFieldMuted : ""}`}
                          >
                            <Text
                              component="span"
                              fw={totalBold ? 600 : undefined}
                              className={li._removed ? classes.lineWas : undefined}
                            >
                              {currencyFormat(lineTot, { withSymbol: false })}
                            </Text>
                            {was?.total != null && (
                              <Text component="span" className={classes.lineWas}>
                                {currencyFormat(was.total, { withSymbol: false })}
                              </Text>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      )
                    })}
                  </Table.Tbody>
                  <Table.Tfoot>
                    {!showProposal && taxAmount > 0 && (
                      <Table.Tr className={classes.tfootRow}>
                        <Table.Td colSpan={4} className={classes.num}>
                          {t("invoice:detail.totals.tax")}
                        </Table.Td>
                        <Table.Td className={`${classes.num} ${classes.amountNowrap}`}>{currencyFormat(taxAmount, { withSymbol: false })}</Table.Td>
                      </Table.Tr>
                    )}
                    {!showProposal && additionalAmount > 0 && (
                      <Table.Tr className={classes.tfootRow}>
                        <Table.Td colSpan={4} className={classes.num}>
                          {t("invoice:detail.totals.additionalLines")}
                        </Table.Td>
                        <Table.Td className={classes.num}>
                          {currencyFormat(additionalAmount, { withSymbol: false })}
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {!showProposal && shippingAmount > 0 && (
                      <Table.Tr className={classes.tfootRow}>
                        <Table.Td colSpan={4} className={classes.num}>
                          {t("invoice:detail.totals.shipping")}
                        </Table.Td>
                        <Table.Td className={classes.num}>
                          {currencyFormat(shippingAmount, { withSymbol: false })}
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {!showProposal && !vatIncluded && vatAmount > 0 && (
                      <Table.Tr className={classes.tfootRow}>
                        <Table.Td colSpan={4} className={classes.num}>
                          {t("invoice:detail.totals.vat")}
                        </Table.Td>
                        <Table.Td className={`${classes.num} ${classes.amountNowrap}`}>{currencyFormat(vatAmount, { withSymbol: false })}</Table.Td>
                      </Table.Tr>
                    )}
                    {!showProposal && reconcileDiscount > 0 && (
                      <Table.Tr className={`${classes.tfootRow} ${classes.tfootDiscount}`}>
                        <Table.Td colSpan={4} className={classes.num}>
                          {t("invoice:detail.totals.discount")}
                          {discountPct > 0 ? ` (${discountPct}%)` : ""}
                        </Table.Td>
                        <Table.Td className={classes.num}>
                          −{currencyFormat(reconcileDiscount, { withSymbol: false })}
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {proposalVatExclusive && (
                      <Table.Tr className={classes.tfootRow}>
                        <Table.Td colSpan={4} className={classes.num}>
                          {t("invoice:detail.totals.vat")}
                        </Table.Td>
                        <Table.Td className={`${classes.num} ${classes.amountNowrap}`}>
                          {currencyFormat(proposalVat!, { withSymbol: false })}
                        </Table.Td>
                      </Table.Tr>
                    )}
                    <Table.Tr className={classes.tfootGrand}>
                      <Table.Td colSpan={4} className={classes.num}>
                        {showProposal ? (
                          <>
                            <Text component="span" className={`${classes.tfootStruck} ${classes.amountNowrap}`}>
                              {t("invoice:amend.linesFooterCurrent")} {currencyFormat(proposalPrevious)}
                            </Text>
                            {" · "}
                            {t("invoice:amend.linesFooterProposal")}
                          </>
                        ) : (
                          t("invoice:detail.totals.grandShort")
                        )}
                      </Table.Td>
                      <Table.Td className={`${classes.num} ${classes.tfootGrandVal}`}>
                        {currencyFormat(showProposal ? proposalGrand : grandTotal)}
                      </Table.Td>
                    </Table.Tr>
                  </Table.Tfoot>
                </Table>
                {!showProposal && vatIncluded && vatAmount > 0 && (
                  <Text className={classes.vatBelow}>
                    {t("invoice:detail.totals.vatOfWhich")} {currencyFormat(vatAmount, { withSymbol: false })}
                  </Text>
                )}
                {proposalVatInclusive && (
                  <Text className={classes.vatBelow}>
                    {t("invoice:detail.totals.vatOfWhich")} {currencyFormat(proposalVat!, { withSymbol: false })}
                  </Text>
                )}
              </Box>
            )}
          </Box>

          {/* 8. Info */}
          <Box className={`${classes.card} ${classes.cardPad} ${classes.orderInfo}`}>
            <Box component="dl" className={classes.metaDl}>
              <Box className={classes.metaItem}>
                <Text component="dt" className={classes.metaDt}>{t("invoice:detail.meta.sender")}</Text>
                <Text component="dd" className={classes.metaDd}>{renderBusinessName(sender, "-")}</Text>
              </Box>
              <Box className={classes.metaItem}>
                <Text component="dt" className={classes.metaDt}>{t("invoice:detail.meta.receiver")}</Text>
                <Text component="dd" className={classes.metaDd}>{receiverLabel}</Text>
              </Box>
              <Box className={classes.metaItem}>
                <Text component="dt" className={classes.metaDt}>{t("invoice:detail.meta.currency")}</Text>
                <Text component="dd" className={classes.metaDd}>MNT</Text>
              </Box>
              <Box className={classes.metaItem}>
                <Text component="dt" className={classes.metaDt}>{t("invoice:detail.meta.createdConfirmed")}</Text>
                <Text component="dd" className={classes.metaDd}>{createdConfirmed}</Text>
              </Box>
              <Box className={classes.metaItem}>
                <Text component="dt" className={classes.metaDt}>{t("invoice:detail.meta.po")}</Text>
                <Text
                  component="dd"
                  className={`${classes.metaDd} ${poCode ? classes.metaMono : classes.metaDdMuted}`}
                >
                  {poCode ?? "-"}
                </Text>
              </Box>
              <Box className={classes.metaItem}>
                <Text component="dt" className={classes.metaDt}>{t("invoice:detail.meta.terms")}</Text>
                <Text component="dd" className={classes.metaDd}>{paymentTerms}</Text>
              </Box>
            </Box>
          </Box>

          {/* 9. Attachments / notes */}
          {hasAttachmentsCard && (
            <Box className={`${classes.card} ${classes.orderAttach}`}>
              <Box className={classes.cardHead}>
                <Title order={2} className={classes.cardTitle}>
                  {t("invoice:detail.attachments")}
                </Title>
              </Box>
              <Box className={classes.attachBody}>
                {descriptionText && <Text className={classes.attachNote}>{descriptionText}</Text>}
                {attachments.map((a: any, i: number) => {
                  const url: string = a.url ?? ""
                  const label = a.name || url.split("/").pop() || t("common:noData")
                  return (
                    <Box key={a.id ?? i} className={classes.fileCard}>
                      <Box component="span" className={classes.typeBadge}>
                        {fileExt(a.name || url)}
                      </Box>
                      <Box className={classes.fileMeta}>
                        <Text className={classes.fileName} title={label}>
                          {label}
                        </Text>
                        {url ? (
                          <Anchor href={url} target="_blank" rel="noopener noreferrer" className={classes.fileLink}>
                            {t("invoice:detail.attachOpen")}
                          </Anchor>
                        ) : null}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            </Box>
          )}

          {/* 10. Amendment history */}
          {amendability?.enabled && (amendability.history?.length ?? 0) > 0 && (
            <Box className={classes.orderAmendHist}>
              <AmendmentHistory items={amendability.history ?? []} />
            </Box>
          )}
        </Box>

        <Box className={classes.colSide}>
          {/* 11. Progress */}
          <Box className={`${classes.card} ${classes.cardPadSide} ${classes.orderProgress}`}>
            <Title order={2} className={classes.cardTitle} mb={2}>
              {t("invoice:detail.progressTitle")}
            </Title>
            <Box className={classes.progressList}>
              {(() => {
                const amendAfterKey = pending
                  ? (stepOrder.find((k) => steps[k] === "active") ??
                    [...stepOrder].reverse().find((k) => steps[k] === "done") ??
                    null)
                  : null
                return stepOrder.map((key) => {
                  const state = steps[key]
                  let label = t(`invoice:detail.stepper.${key}`)
                  if (key === "paid" && state === "active") {
                    label = t("invoice:detail.stepper.payingPct", { pct: paidPctRounded })
                  }
                  return (
                    <Box key={key}>
                      <Box
                        className={`${classes.progressRow} ${
                          state === "todo"
                            ? classes.progressRowTodo
                            : state === "active"
                              ? classes.progressRowActive
                              : ""
                        }`}
                      >
                        <Box
                          className={`${classes.progressDot} ${
                            state === "done"
                              ? classes.progressDotDone
                              : state === "active"
                                ? classes.progressDotActive
                                : classes.progressDotTodo
                          }`}
                        >
                          {state === "done" ? <CheckIcon size={11} weight="bold" /> : null}
                        </Box>
                        <Text component="span" fz={14} fw={state === "active" ? 600 : 400}>
                          {label}
                        </Text>
                      </Box>
                      {amendAfterKey === key && (
                        <Box className={`${classes.progressRow} ${classes.progressRowActive}`}>
                          <Box className={`${classes.progressDot} ${classes.progressDotAmend}`} />
                          <Text component="span" fz={14} fw={600}>
                            {t("invoice:amend.progressPending")}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  )
                })
              })()}
            </Box>
          </Box>

          {/* 12. Contact */}
          <Box className={`${classes.card} ${classes.cardPadSide} ${classes.orderContact}`}>
            <Title order={2} className={classes.cardTitle} mb={4}>
              {t("invoice:detail.contactTitle")}
            </Title>
            <Box component="dl" className={classes.contactDl}>
              <Box className={classes.metaItem}>
                <Text component="dt" className={classes.metaDt}>{t("invoice:detail.meta.receiver")}</Text>
                <Text component="dd" className={classes.metaDd}>{renderBusinessName(receiver, "-")}</Text>
              </Box>
              {contactEmail && (
                <Box className={classes.metaItem}>
                  <Text component="dt" className={classes.metaDt}>{t("invoice:detail.contact.email")}</Text>
                  <Text component="dd" className={classes.metaDd}>{contactEmail}</Text>
                </Box>
              )}
              {contactPhone && (
                <Box className={classes.metaItem}>
                  <Text component="dt" className={classes.metaDt}>{t("invoice:detail.contact.phone")}</Text>
                  <Text component="dd" className={classes.metaDd}>{contactPhone}</Text>
                </Box>
              )}
              {contactAddress && (
                <Box className={classes.metaItem}>
                  <Text component="dt" className={classes.metaDt}>{t("invoice:detail.contact.address")}</Text>
                  <Text component="dd" className={classes.metaDd}>{contactAddress}</Text>
                </Box>
              )}
              {!contactEmail && !contactPhone && !contactAddress && (
                <Text fz={14} c="gray.7">{t("common:noData")}</Text>
              )}
            </Box>
          </Box>

          {/* 13. History */}
          <Box className={`${classes.historyCard} ${classes.cardPadSide} ${classes.orderHistory}`}>
            <Title order={2} className={classes.cardTitle} mb={4}>
              {t("invoice:detail.history.title")}
            </Title>
            <Box className={classes.timeline}>
              {pending && (
                <Box className={classes.tlItem}>
                  <Box className={`${classes.tlDot} ${classes.tlDotAmendPending}`} />
                  <Box>
                    <Text className={classes.tlTitle}>
                      {t("invoice:amend.historyProposed", { ref: proposalCode })}
                    </Text>
                    <Text className={classes.tlSub}>
                      {dateTimeFormat(pending.proposedAt)}
                      {" · "}
                      {t("invoice:amend.historyTotals", {
                        from: currencyFormat(pending.previousTotal, { withSymbol: false }),
                        to: currencyFormat(pending.newTotal, { withSymbol: false }),
                      })}
                    </Text>
                  </Box>
                </Box>
              )}
              {d.paymentStatus === "CLOSED" && d.paidDate && (
                <Box className={classes.tlItem}>
                  <Box className={`${classes.tlDot} ${classes.tlDotPay}`} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:detail.history.paid")}</Text>
                    <Text className={classes.tlSub}>{dateTimeFormat(d.paidDate)}</Text>
                  </Box>
                </Box>
              )}
              {d.paymentStatus === "DIVIDED" && d.paidDate && (
                <Box className={classes.tlItem}>
                  <Box className={`${classes.tlDot} ${classes.tlDotPay}`} />
                  <Box>
                    <Text className={classes.tlTitle}>
                      {t("invoice:detail.history.partialPaid", {
                        amount: currencyFormat(paid),
                      })}
                    </Text>
                    <Text className={classes.tlSub}>{dateTimeFormat(d.paidDate)}</Text>
                  </Box>
                </Box>
              )}
              {(amendability?.history ?? []).map((h) => {
                const dotCls =
                  h.status === "REJECTED"
                    ? classes.tlDotAmendRejected
                    : h.status === "WITHDRAWN"
                      ? classes.tlDotAmendWithdrawn
                      : classes.tlDotAmend
                return (
                  <Box key={h.id} className={classes.tlItem}>
                    <Box className={`${classes.tlDot} ${dotCls}`} />
                    <Box>
                      <Text className={classes.tlTitle}>
                        {t(`invoice:amend.status.${h.status}`)} · {h.refCode}
                      </Text>
                      <Text className={classes.tlSub}>
                        {dateTimeFormat(h.respondedAt ?? h.proposedAt)}
                        {" · "}
                        {t("invoice:amend.historyTotals", {
                          from: currencyFormat(h.previousTotal, { withSymbol: false }),
                          to: currencyFormat(h.newTotal, { withSymbol: false }),
                        })}
                      </Text>
                    </Box>
                  </Box>
                )
              })}
              {d.confirmedDate && (
                <Box className={classes.tlItem}>
                  <Box className={classes.tlDot} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:detail.history.confirmed")}</Text>
                    <Text className={classes.tlSub}>
                      {[d.confirmedUser ? renderName(d.confirmedUser) : null, dateTimeFormat(d.confirmedDate)]
                        .filter(Boolean)
                        .join(" · ")}
                    </Text>
                  </Box>
                </Box>
              )}
              {d.viewedDate && (
                <Box className={classes.tlItem}>
                  <Box className={classes.tlDot} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:detail.history.viewed")}</Text>
                    <Text className={classes.tlSub}>{dateTimeFormat(d.viewedDate)}</Text>
                  </Box>
                </Box>
              )}
              {d.sentDate && (
                <Box className={classes.tlItem}>
                  <Box className={classes.tlDot} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:detail.history.sent")}</Text>
                    <Text className={classes.tlSub}>
                      {[d.senderUser ? renderName(d.senderUser) : null, dateTimeFormat(d.sentDate)]
                        .filter(Boolean)
                        .join(" · ")}
                    </Text>
                  </Box>
                </Box>
              )}
              {(d.createdAt || (!d.sentDate && !d.confirmedDate)) && (
                <Box className={classes.tlItem}>
                  <Box className={classes.tlDot} />
                  <Box>
                    <Text className={classes.tlTitle}>{t("invoice:detail.history.created")}</Text>
                    {d.createdAt && <Text className={classes.tlSub}>{dateTimeFormat(d.createdAt)}</Text>}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* 14. Related returns */}
          {params.id && params.id !== "new" ? (
            <Box className={classes.orderReturns}>
              <SafeRelatedReturns
                httpClient={httpRequest}
                target={{ kind: "invoice", invoiceId: params.id }}
                orderAdminBaseUrl={getModuleUrl("order")}
              />
            </Box>
          ) : null}
        </Box>
      </Box>

      <Drawer
        size="lg"
        opened={action[0] === "payment_register"}
        onClose={() => setAction([])}
        withCloseButton={false}
        title={false}
        classNames={{ body: modalChrome.drawerBodyFlush }}
        styles={REGISTER_PAYMENT_DRAWER_STYLES}
      >
        <RegisterPayment action={action} setAction={setAction} reload={async () => { await mutate() }} />
      </Drawer>

      {params.id && params.id !== "new" && (
        <DeliverCopyModal
          invoiceId={`${params.id}`}
          opened={copyOpened}
          onClose={() => setCopyOpened(false)}
        />
      )}

      {params.id && params.id !== "new" && (
        <ReminderDrawer
          invoiceId={`${params.id}`}
          opened={reminderOpened}
          onClose={() => setReminderOpened(false)}
        />
      )}
    </Page>
  )
}
